<template>
  <div id="closet-clothing-detail">
    <div v-if="clothing" class="clothing-layout">
      <!-- 좌측: 상세 정보 -->
      <div class="clothing-detail-left">
        <div class="detail-section">
          <div class="detail-header">
            <div class="detail-title-row">
              <span class="badge category">{{ getCategoryLabel(clothing.category) }}</span>
              <h3 class="detail-brand">{{ clothing.brandName }}</h3>
              <span class="detail-product">{{ clothing.productName || clothing.subCategory }}</span>
            </div>
            <div class="detail-actions">
              <button class="btn-edit" @click="goToEdit">수정</button>
              <button class="btn-delete" @click="handleDelete">삭제</button>
            </div>
          </div>

          <!-- 기본 정보 -->
          <div class="detail-info-grid">
            <div class="info-item" v-if="clothing.subCategory">
              <span class="info-label">소분류</span>
              <span class="info-value">{{ clothing.subCategory }}</span>
            </div>
            <div class="info-item" v-if="clothing.labeledSize">
              <span class="info-label">표기 사이즈</span>
              <span class="info-value size-badge">{{ clothing.labeledSize }}</span>
            </div>
            <div class="info-item" v-if="clothing.purchaseDate">
              <span class="info-label">구매일</span>
              <span class="info-value">{{ formatDate(clothing.purchaseDate) }}</span>
            </div>
            <div class="info-item" v-if="clothing.satisfaction">
              <span class="info-label">만족도</span>
              <span class="info-value">
                <span v-for="s in 5" :key="s" class="star-sm" :class="{ active: s <= clothing.satisfaction }">&#9733;</span>
              </span>
            </div>
            <div class="info-item" v-if="clothing.fitFeedback">
              <span class="info-label">핏 평가</span>
              <span class="fit-chip" :class="fitClass(clothing.fitFeedback)">{{ getFitFeedbackLabel(clothing.fitFeedback) }}</span>
            </div>
          </div>

          <!-- 실측 치수 -->
          <div class="measure-detail" v-if="clothing.measurements && Object.keys(clothing.measurements).length > 0">
            <div class="measure-detail-header">
              <div class="measure-detail-title">실측 치수</div>
              <button class="unit-toggle" @click="toggleUnit">
                <span :class="{ on: !toInch }">cm</span>
                <span class="sep">/</span>
                <span :class="{ on: toInch }">in</span>
              </button>
            </div>
            <div class="measure-tags">
              <div v-for="(val, key) in clothing.measurements" :key="key" class="measure-item">
                <span class="measure-label">{{ getMeasureLabel(clothing.category, String(key)) }}</span>
                <span class="measure-value">{{ dispMeasureVal(clothing.category, String(key), Number(val)) }}{{ dispMeasureUnit(clothing.category, String(key)) }}</span>
              </div>
            </div>
          </div>

          <!-- 메모 -->
          <div class="memo-section" v-if="clothing.memo">
            <div class="memo-title">메모</div>
            <div class="memo-content">{{ clothing.memo }}</div>
          </div>

          <div class="detail-footer">
            <button class="btn-back" @click="goToList">목록으로</button>
          </div>
        </div>
      </div>

      <!-- 우측: SVG 의류 도형 (읽기 전용) -->
      <div class="clothing-svg-section" v-if="hasMeasurements">
        <div class="svg-card">
          <div class="svg-title">의류 치수 다이어그램</div>
          <div class="svg-container">
            <!-- TOP / OUTER -->
            <svg v-if="svgCategory === 'TOP'" viewBox="0 0 300 360" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="dtTopGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#dce8f5"/>
                  <stop offset="100%" stop-color="#eef4fc"/>
                </linearGradient>
              </defs>
              <path :d="topSvgPath" fill="url(#dtTopGrad)" stroke="#4a90d9" stroke-width="2" stroke-linejoin="round"/>
              <!-- 총장 -->
              <g v-if="ms.total_length">
                <line :x1="topCoords.leftEdge - 20" :y1="topCoords.topY" :x2="topCoords.leftEdge - 20" :y2="topCoords.bottomY" stroke="#888" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="topCoords.leftEdge - 24" :y1="topCoords.topY" :x2="topCoords.leftEdge - 16" :y2="topCoords.topY" stroke="#888" stroke-width="1"/>
                <line :x1="topCoords.leftEdge - 24" :y1="topCoords.bottomY" :x2="topCoords.leftEdge - 16" :y2="topCoords.bottomY" stroke="#888" stroke-width="1"/>
                <rect :x="topCoords.leftEdge - 50" :y="(topCoords.topY + topCoords.bottomY) / 2 - 8" width="56" height="16" rx="3" fill="#fff" stroke="#ddd" stroke-width="0.5"/>
                <text :x="topCoords.leftEdge - 22" :y="(topCoords.topY + topCoords.bottomY) / 2 + 4" text-anchor="middle" class="svg-measure-label">총장 {{ dcm(ms.total_length) }}{{ lenU }}</text>
              </g>
              <!-- 어깨 -->
              <g v-if="ms.shoulder_width">
                <line :x1="topCoords.shoulderL" :y1="topCoords.topY - 16" :x2="topCoords.shoulderR" :y2="topCoords.topY - 16" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="topCoords.shoulderL" :y1="topCoords.topY - 20" :x2="topCoords.shoulderL" :y2="topCoords.topY - 12" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="topCoords.shoulderR" :y1="topCoords.topY - 20" :x2="topCoords.shoulderR" :y2="topCoords.topY - 12" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="150 - 34" :y="topCoords.topY - 34" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text x="150" :y="topCoords.topY - 23" text-anchor="middle" class="svg-measure-label red">어깨 {{ dcm(ms.shoulder_width) }}{{ lenU }}</text>
              </g>
              <!-- 가슴 -->
              <g v-if="ms.chest_width">
                <line :x1="topCoords.chestL" :y1="topCoords.chestY" :x2="topCoords.chestR" :y2="topCoords.chestY" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="topCoords.chestL" :y1="topCoords.chestY - 4" :x2="topCoords.chestL" :y2="topCoords.chestY + 4" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="topCoords.chestR" :y1="topCoords.chestY - 4" :x2="topCoords.chestR" :y2="topCoords.chestY + 4" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="(topCoords.chestL + topCoords.chestR) / 2 - 34" :y="topCoords.chestY + 8" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text :x="(topCoords.chestL + topCoords.chestR) / 2" :y="topCoords.chestY + 20" text-anchor="middle" class="svg-measure-label red">가슴 {{ dcm(ms.chest_width) }}{{ lenU }}</text>
              </g>
              <!-- 소매 -->
              <g v-if="ms.sleeve_length">
                <line :x1="topCoords.shoulderR" :y1="topCoords.topY" :x2="topCoords.sleeveEndX" :y2="topCoords.sleeveEndY" stroke="#3498db" stroke-width="0.8" stroke-dasharray="4,2"/>
                <rect :x="(topCoords.shoulderR + topCoords.sleeveEndX) / 2 - 34" :y="(topCoords.topY + topCoords.sleeveEndY) / 2 - 18" width="68" height="16" rx="3" fill="#fff" stroke="#c0d8f0" stroke-width="0.5"/>
                <text :x="(topCoords.shoulderR + topCoords.sleeveEndX) / 2" :y="(topCoords.topY + topCoords.sleeveEndY) / 2 - 6" text-anchor="middle" class="svg-measure-label blue">소매 {{ dcm(ms.sleeve_length) }}{{ lenU }}</text>
              </g>
              <!-- 밑단 -->
              <g v-if="ms.hem_width">
                <line :x1="topCoords.hemL" :y1="topCoords.bottomY + 12" :x2="topCoords.hemR" :y2="topCoords.bottomY + 12" stroke="#27ae60" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="topCoords.hemL" :y1="topCoords.bottomY + 8" :x2="topCoords.hemL" :y2="topCoords.bottomY + 16" stroke="#27ae60" stroke-width="1"/>
                <line :x1="topCoords.hemR" :y1="topCoords.bottomY + 8" :x2="topCoords.hemR" :y2="topCoords.bottomY + 16" stroke="#27ae60" stroke-width="1"/>
                <rect :x="150 - 30" :y="topCoords.bottomY + 18" width="60" height="16" rx="3" fill="#fff" stroke="#b0d8b0" stroke-width="0.5"/>
                <text x="150" :y="topCoords.bottomY + 30" text-anchor="middle" class="svg-measure-label green">밑단 {{ dcm(ms.hem_width) }}{{ lenU }}</text>
              </g>
              <!-- 암홀 (소매 두께: 어깨→겨드랑이 수직거리) -->
              <g v-if="ms.armhole">
                <line :x1="topCoords.shoulderR" :y1="topCoords.topY" :x2="topCoords.rArmX" :y2="topCoords.rArmY" stroke="#8e44ad" stroke-width="0.8" stroke-dasharray="4,2"/>
                <circle :cx="topCoords.shoulderR" :cy="topCoords.topY" r="2" fill="#8e44ad"/>
                <circle :cx="topCoords.rArmX" :cy="topCoords.rArmY" r="2" fill="#8e44ad"/>
                <rect :x="topCoords.shoulderR - 68" :y="(topCoords.topY + topCoords.rArmY) / 2 - 8" width="62" height="16" rx="3" fill="#fff" stroke="#d8c0f0" stroke-width="0.5"/>
                <text :x="topCoords.shoulderR - 37" :y="(topCoords.topY + topCoords.rArmY) / 2 + 4" text-anchor="middle" class="svg-measure-label purple">암홀 {{ dcm(ms.armhole) }}{{ lenU }}</text>
              </g>
            </svg>

            <!-- BOTTOM -->
            <svg v-else-if="svgCategory === 'BOTTOM'" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="dtBtmGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#dce8f5"/>
                  <stop offset="100%" stop-color="#eef4fc"/>
                </linearGradient>
              </defs>
              <path :d="bottomSvgPath" fill="url(#dtBtmGrad)" stroke="#4a90d9" stroke-width="2" stroke-linejoin="round"/>
              <!-- 총장 -->
              <g v-if="ms.total_length">
                <line :x1="btmCoords.leftEdge - 20" :y1="btmCoords.topY" :x2="btmCoords.leftEdge - 20" :y2="btmCoords.bottomY" stroke="#888" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.leftEdge - 24" :y1="btmCoords.topY" :x2="btmCoords.leftEdge - 16" :y2="btmCoords.topY" stroke="#888" stroke-width="1"/>
                <line :x1="btmCoords.leftEdge - 24" :y1="btmCoords.bottomY" :x2="btmCoords.leftEdge - 16" :y2="btmCoords.bottomY" stroke="#888" stroke-width="1"/>
                <rect :x="btmCoords.leftEdge - 50" :y="(btmCoords.topY + btmCoords.bottomY) / 2 - 8" width="56" height="16" rx="3" fill="#fff" stroke="#ddd" stroke-width="0.5"/>
                <text :x="btmCoords.leftEdge - 22" :y="(btmCoords.topY + btmCoords.bottomY) / 2 + 4" text-anchor="middle" class="svg-measure-label">총장 {{ dcm(ms.total_length) }}{{ lenU }}</text>
              </g>
              <!-- 허리 -->
              <g v-if="ms.waist_width">
                <line :x1="btmCoords.waistL" :y1="btmCoords.topY - 14" :x2="btmCoords.waistR" :y2="btmCoords.topY - 14" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.waistL" :y1="btmCoords.topY - 18" :x2="btmCoords.waistL" :y2="btmCoords.topY - 10" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="btmCoords.waistR" :y1="btmCoords.topY - 18" :x2="btmCoords.waistR" :y2="btmCoords.topY - 10" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="150 - 34" :y="btmCoords.topY - 32" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text x="150" :y="btmCoords.topY - 21" text-anchor="middle" class="svg-measure-label red">허리 {{ dcm(ms.waist_width) }}{{ lenU }}</text>
              </g>
              <!-- 엉덩이 -->
              <g v-if="ms.hip_width">
                <line :x1="btmCoords.hipL" :y1="btmCoords.hipY" :x2="btmCoords.hipR" :y2="btmCoords.hipY" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.hipL" :y1="btmCoords.hipY - 4" :x2="btmCoords.hipL" :y2="btmCoords.hipY + 4" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="btmCoords.hipR" :y1="btmCoords.hipY - 4" :x2="btmCoords.hipR" :y2="btmCoords.hipY + 4" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="btmCoords.hipR + 6" :y="btmCoords.hipY - 8" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text :x="btmCoords.hipR + 40" :y="btmCoords.hipY + 4" text-anchor="middle" class="svg-measure-label red">엉덩이 {{ dcm(ms.hip_width) }}{{ lenU }}</text>
              </g>
              <!-- 허벅지 -->
              <g v-if="ms.thigh_width">
                <line :x1="btmCoords.thighL" :y1="btmCoords.thighY" :x2="btmCoords.thighMid" :y2="btmCoords.thighY" stroke="#3498db" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.thighL" :y1="btmCoords.thighY - 4" :x2="btmCoords.thighL" :y2="btmCoords.thighY + 4" stroke="#3498db" stroke-width="1"/>
                <line :x1="btmCoords.thighMid" :y1="btmCoords.thighY - 4" :x2="btmCoords.thighMid" :y2="btmCoords.thighY + 4" stroke="#3498db" stroke-width="1"/>
                <rect :x="btmCoords.thighL - 4" :y="btmCoords.thighY + 6" width="72" height="16" rx="3" fill="#fff" stroke="#c0d8f0" stroke-width="0.5"/>
                <text :x="btmCoords.thighL + 32" :y="btmCoords.thighY + 18" text-anchor="middle" class="svg-measure-label blue">허벅지 {{ dcm(ms.thigh_width) }}{{ lenU }}</text>
              </g>
              <!-- 밑위 -->
              <g v-if="ms.rise">
                <line :x1="btmCoords.waistR + 16" :y1="btmCoords.topY" :x2="btmCoords.waistR + 16" :y2="btmCoords.crotchY" stroke="#27ae60" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.waistR + 12" :y1="btmCoords.topY" :x2="btmCoords.waistR + 20" :y2="btmCoords.topY" stroke="#27ae60" stroke-width="1"/>
                <line :x1="btmCoords.waistR + 12" :y1="btmCoords.crotchY" :x2="btmCoords.waistR + 20" :y2="btmCoords.crotchY" stroke="#27ae60" stroke-width="1"/>
                <rect :x="btmCoords.waistR + 22" :y="(btmCoords.topY + btmCoords.crotchY) / 2 - 8" width="56" height="16" rx="3" fill="#fff" stroke="#b0d8b0" stroke-width="0.5"/>
                <text :x="btmCoords.waistR + 50" :y="(btmCoords.topY + btmCoords.crotchY) / 2 + 4" text-anchor="middle" class="svg-measure-label green">밑위 {{ dcm(ms.rise) }}{{ lenU }}</text>
              </g>
              <!-- 밑단 -->
              <g v-if="ms.hem_width">
                <line :x1="btmCoords.hemL" :y1="btmCoords.bottomY + 12" :x2="btmCoords.hemMid" :y2="btmCoords.bottomY + 12" stroke="#27ae60" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.hemL" :y1="btmCoords.bottomY + 8" :x2="btmCoords.hemL" :y2="btmCoords.bottomY + 16" stroke="#27ae60" stroke-width="1"/>
                <line :x1="btmCoords.hemMid" :y1="btmCoords.bottomY + 8" :x2="btmCoords.hemMid" :y2="btmCoords.bottomY + 16" stroke="#27ae60" stroke-width="1"/>
                <rect :x="btmCoords.hemL - 4" :y="btmCoords.bottomY + 20" width="60" height="16" rx="3" fill="#fff" stroke="#b0d8b0" stroke-width="0.5"/>
                <text :x="btmCoords.hemL + 26" :y="btmCoords.bottomY + 32" text-anchor="middle" class="svg-measure-label green">밑단 {{ dcm(ms.hem_width) }}{{ lenU }}</text>
              </g>
            </svg>

            <!-- ONEPIECE -->
            <svg v-else-if="svgCategory === 'ONEPIECE'" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="dtOpGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#f0dce8"/>
                  <stop offset="100%" stop-color="#fceef4"/>
                </linearGradient>
              </defs>
              <path :d="onepieceSvgPath" fill="url(#dtOpGrad)" stroke="#c0608a" stroke-width="2" stroke-linejoin="round"/>
              <!-- 총장 -->
              <g v-if="ms.total_length">
                <line :x1="opCoords.leftEdge - 20" :y1="opCoords.topY" :x2="opCoords.leftEdge - 20" :y2="opCoords.bottomY" stroke="#888" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="opCoords.leftEdge - 24" :y1="opCoords.topY" :x2="opCoords.leftEdge - 16" :y2="opCoords.topY" stroke="#888" stroke-width="1"/>
                <line :x1="opCoords.leftEdge - 24" :y1="opCoords.bottomY" :x2="opCoords.leftEdge - 16" :y2="opCoords.bottomY" stroke="#888" stroke-width="1"/>
                <rect :x="opCoords.leftEdge - 50" :y="(opCoords.topY + opCoords.bottomY) / 2 - 8" width="56" height="16" rx="3" fill="#fff" stroke="#ddd" stroke-width="0.5"/>
                <text :x="opCoords.leftEdge - 22" :y="(opCoords.topY + opCoords.bottomY) / 2 + 4" text-anchor="middle" class="svg-measure-label">총장 {{ dcm(ms.total_length) }}{{ lenU }}</text>
              </g>
              <!-- 어깨 -->
              <g v-if="ms.shoulder_width">
                <line :x1="opCoords.shoulderL" :y1="opCoords.topY - 14" :x2="opCoords.shoulderR" :y2="opCoords.topY - 14" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="opCoords.shoulderL" :y1="opCoords.topY - 18" :x2="opCoords.shoulderL" :y2="opCoords.topY - 10" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="opCoords.shoulderR" :y1="opCoords.topY - 18" :x2="opCoords.shoulderR" :y2="opCoords.topY - 10" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="150 - 34" :y="opCoords.topY - 32" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text x="150" :y="opCoords.topY - 21" text-anchor="middle" class="svg-measure-label red">어깨 {{ dcm(ms.shoulder_width) }}{{ lenU }}</text>
              </g>
              <!-- 가슴 -->
              <g v-if="ms.chest_width">
                <line :x1="opCoords.chestL" :y1="opCoords.chestY" :x2="opCoords.chestR" :y2="opCoords.chestY" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <rect :x="opCoords.chestR + 6" :y="opCoords.chestY - 8" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text :x="opCoords.chestR + 40" :y="opCoords.chestY + 4" text-anchor="middle" class="svg-measure-label red">가슴 {{ dcm(ms.chest_width) }}{{ lenU }}</text>
              </g>
              <!-- 허리 -->
              <g v-if="ms.waist_width">
                <line :x1="opCoords.waistL" :y1="opCoords.waistY" :x2="opCoords.waistR" :y2="opCoords.waistY" stroke="#3498db" stroke-width="0.8" stroke-dasharray="4,2"/>
                <rect :x="opCoords.waistR + 6" :y="opCoords.waistY - 8" width="68" height="16" rx="3" fill="#fff" stroke="#c0d8f0" stroke-width="0.5"/>
                <text :x="opCoords.waistR + 40" :y="opCoords.waistY + 4" text-anchor="middle" class="svg-measure-label blue">허리 {{ dcm(ms.waist_width) }}{{ lenU }}</text>
              </g>
              <!-- 엉덩이 -->
              <g v-if="ms.hip_width">
                <line :x1="opCoords.hipL" :y1="opCoords.hipY" :x2="opCoords.hipR" :y2="opCoords.hipY" stroke="#27ae60" stroke-width="0.8" stroke-dasharray="4,2"/>
                <rect :x="opCoords.hipR + 6" :y="opCoords.hipY - 8" width="72" height="16" rx="3" fill="#fff" stroke="#b0d8b0" stroke-width="0.5"/>
                <text :x="opCoords.hipR + 42" :y="opCoords.hipY + 4" text-anchor="middle" class="svg-measure-label green">엉덩이 {{ dcm(ms.hip_width) }}{{ lenU }}</text>
              </g>
              <!-- 소매 -->
              <g v-if="ms.sleeve_length">
                <line :x1="opCoords.shoulderR" :y1="opCoords.topY" :x2="opCoords.sleeveEndX" :y2="opCoords.sleeveEndY" stroke="#3498db" stroke-width="0.8" stroke-dasharray="4,2"/>
                <rect :x="opCoords.sleeveEndX - 4" :y="opCoords.sleeveEndY + 4" width="68" height="16" rx="3" fill="#fff" stroke="#c0d8f0" stroke-width="0.5"/>
                <text :x="opCoords.sleeveEndX + 30" :y="opCoords.sleeveEndY + 16" text-anchor="middle" class="svg-measure-label blue">소매 {{ dcm(ms.sleeve_length) }}{{ lenU }}</text>
              </g>
            </svg>

            <!-- SHOES -->
            <svg v-else-if="svgCategory === 'SHOES'" viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="dtShoeGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stop-color="#e8e0d4"/>
                  <stop offset="100%" stop-color="#f5f0e8"/>
                </linearGradient>
              </defs>
              <path :d="shoesSvgPath" fill="url(#dtShoeGrad)" stroke="#8a7560" stroke-width="2" stroke-linejoin="round"/>
              <!-- 발길이 -->
              <g v-if="ms.foot_length">
                <line :x1="shoeCoords.toeX" :y1="shoeCoords.bottomY + 16" :x2="shoeCoords.heelX" :y2="shoeCoords.bottomY + 16" stroke="#888" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="shoeCoords.toeX" :y1="shoeCoords.bottomY + 12" :x2="shoeCoords.toeX" :y2="shoeCoords.bottomY + 20" stroke="#888" stroke-width="1"/>
                <line :x1="shoeCoords.heelX" :y1="shoeCoords.bottomY + 12" :x2="shoeCoords.heelX" :y2="shoeCoords.bottomY + 20" stroke="#888" stroke-width="1"/>
                <rect :x="(shoeCoords.toeX + shoeCoords.heelX) / 2 - 34" :y="shoeCoords.bottomY + 22" width="68" height="16" rx="3" fill="#fff" stroke="#ddd" stroke-width="0.5"/>
                <text :x="(shoeCoords.toeX + shoeCoords.heelX) / 2" :y="shoeCoords.bottomY + 34" text-anchor="middle" class="svg-measure-label">발길이 {{ dmm(ms.foot_length) }}{{ mmU }}</text>
              </g>
              <!-- 발볼 -->
              <g v-if="ms.ball_width">
                <line :x1="shoeCoords.ballX" :y1="shoeCoords.ballTopY" :x2="shoeCoords.ballX" :y2="shoeCoords.bottomY" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="shoeCoords.ballX - 4" :y1="shoeCoords.ballTopY" :x2="shoeCoords.ballX + 4" :y2="shoeCoords.ballTopY" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="shoeCoords.ballX - 4" :y1="shoeCoords.bottomY" :x2="shoeCoords.ballX + 4" :y2="shoeCoords.bottomY" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="shoeCoords.ballX + 8" :y="(shoeCoords.ballTopY + shoeCoords.bottomY) / 2 - 8" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text :x="shoeCoords.ballX + 42" :y="(shoeCoords.ballTopY + shoeCoords.bottomY) / 2 + 4" text-anchor="middle" class="svg-measure-label red">발볼 {{ dmm(ms.ball_width) }}{{ mmU }}</text>
              </g>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="loading-message">불러오는 중...</div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
  ClosetClothing,
  getCategoryLabel,
  getFitFeedbackLabel,
  getMeasureLabel,
  MEASUREMENT_FIELDS,
  convertMeasure,
  displayUnit,
  UNIT_STORAGE_KEY,
} from "../class";
import * as closetApi from "../api";

const TOP_DEFAULTS = { total_length: 70, shoulder_width: 50, chest_width: 52, sleeve_length: 60, hem_width: 50, armhole: 22 };
const BOTTOM_DEFAULTS = { total_length: 100, waist_width: 38, hip_width: 48, thigh_width: 30, rise: 28, hem_width: 18 };
const ONEPIECE_DEFAULTS = { total_length: 100, shoulder_width: 42, chest_width: 46, waist_width: 36, hip_width: 48, sleeve_length: 55 };
const SHOES_DEFAULTS = { foot_length: 265, ball_width: 98 };

const clampScale = (val: number | null | undefined, def: number): number => {
  if (!val) return 1;
  return Math.max(0.5, Math.min(2.0, val / def));
};

export default defineComponent({
  name: "ClosetClothingDetailView",
  props: {
    id: { type: [String, Number], required: true },
  },

  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const clothing = ref<ClosetClothing | null>(null);

    const ms = computed(() => clothing.value?.measurements || {});

    // 인치/cm 토글 (localStorage 유지)
    const toInch = ref<boolean>(localStorage.getItem(UNIT_STORAGE_KEY) === "in");
    const toggleUnit = () => {
      toInch.value = !toInch.value;
      localStorage.setItem(UNIT_STORAGE_KEY, toInch.value ? "in" : "cm");
    };
    // SVG 라벨용 단위 헬퍼 (길이=cm, 신발=mm 기준)
    const lenU = computed(() => displayUnit("cm", toInch.value));
    const mmU = computed(() => displayUnit("mm", toInch.value));
    const dcm = (v: number) => convertMeasure(v, "cm", toInch.value);
    const dmm = (v: number) => convertMeasure(v, "mm", toInch.value);

    // 복귀 시 유지할 카테고리 필터
    const wardrobeQuery = () => {
      const c = route.query.category as string;
      return c ? { category: c } : {};
    };

    const svgCategory = computed(() => {
      if (!clothing.value) return "TOP";
      const cat = clothing.value.category;
      if (cat === "OUTER") return "TOP";
      return cat;
    });

    const hasMeasurements = computed(() => {
      return Object.values(ms.value).some((v) => v != null && Number(v) > 0);
    });

    // ===== TOP coords =====
    const topCoords = computed(() => {
      const m = ms.value;
      const sw = clampScale(m.shoulder_width, TOP_DEFAULTS.shoulder_width) * 130;
      const cw = clampScale(m.chest_width, TOP_DEFAULTS.chest_width) * 140;
      const hw = clampScale(m.hem_width, TOP_DEFAULTS.hem_width) * 130;
      const tl = clampScale(m.total_length, TOP_DEFAULTS.total_length) * 240;
      const sl = clampScale(m.sleeve_length, TOP_DEFAULTS.sleeve_length) * 190;
      const ah = clampScale(m.armhole, TOP_DEFAULTS.armhole) * 35;

      const cx = 150;
      const topY = 50;
      const bottomY = topY + Math.min(tl, 300);
      const shoulderL = cx - sw / 2;
      const shoulderR = cx + sw / 2;
      const chestL = cx - cw / 2;
      const chestR = cx + cw / 2;
      const hemL = cx - hw / 2;
      const hemR = cx + hw / 2;
      const chestY = topY + Math.min(tl * 0.25, 80);

      // 소매 끝점 — 수평에 가깝게 펼쳐서 직사각형 형태
      const sleeveEndX = Math.min(shoulderR + sl * 0.55, 290);
      const sleeveEndY = Math.min(topY + sl * 0.65, bottomY);
      const sleeveEndLX = Math.max(shoulderL - sl * 0.55, 10);

      // 소매 방향 수직 벡터 (소매 끝 90도 컷)
      const rDx = sleeveEndX - shoulderR;
      const rDy = sleeveEndY - topY;
      const rLen = Math.sqrt(rDx * rDx + rDy * rDy) || 1;
      const rpX = -rDy / rLen;
      const rpY = rDx / rLen;
      // 소매 끝 하단: 수직 ah (90도 컷)
      const rCuffBotX = sleeveEndX + rpX * ah;
      const rCuffBotY = sleeveEndY + rpY * ah;
      // 겨드랑이: 어깨 바로 아래로만 이동, 소매 두께 균일 보정 (ah/rpY)
      const rArmX = shoulderR;
      const rArmY = Math.min(rpY > 0.01 ? topY + ah / rpY : topY + ah, bottomY - 10);

      // 좌측 소매 (대칭)
      const lDx = sleeveEndLX - shoulderL;
      const lDy = sleeveEndY - topY;
      const lLen = Math.sqrt(lDx * lDx + lDy * lDy) || 1;
      const lpX = lDy / lLen;
      const lpY = -lDx / lLen;
      const lCuffBotX = sleeveEndLX + lpX * ah;
      const lCuffBotY = sleeveEndY + lpY * ah;
      // 겨드랑이: 어깨 바로 아래로만 이동, 소매 두께 균일 보정
      const lArmX = shoulderL;
      const lArmY = Math.min(lpY > 0.01 ? topY + ah / lpY : topY + ah, bottomY - 10);

      const leftEdge = Math.min(shoulderL, chestL, hemL, sleeveEndLX, lCuffBotX);

      return {
        topY, bottomY, shoulderL, shoulderR, chestL, chestR, hemL, hemR, chestY,
        sleeveEndX, sleeveEndY, sleeveEndLX,
        rArmX, rArmY, rCuffBotX, rCuffBotY,
        lArmX, lArmY, lCuffBotX, lCuffBotY,
        leftEdge, sw, cw, hw, sl, ah,
      };
    });

    const topSvgPath = computed(() => {
      const c = topCoords.value;
      const cx = 150;
      const neckW = 14;

      return `M ${cx - neckW},${c.topY}
        Q ${cx},${c.topY - 6} ${cx + neckW},${c.topY}
        L ${c.shoulderR},${c.topY}
        L ${c.sleeveEndX},${c.sleeveEndY}
        L ${c.rCuffBotX},${c.rCuffBotY}
        L ${c.rArmX},${c.rArmY}
        L ${c.hemR},${c.rArmY}
        L ${c.hemR},${c.bottomY}
        L ${c.hemL},${c.bottomY}
        L ${c.hemL},${c.lArmY}
        L ${c.lArmX},${c.lArmY}
        L ${c.lCuffBotX},${c.lCuffBotY}
        L ${c.sleeveEndLX},${c.sleeveEndY}
        L ${c.shoulderL},${c.topY}
        Z`;
    });

    // ===== BOTTOM coords =====
    const btmCoords = computed(() => {
      const m = ms.value;
      const ww = clampScale(m.waist_width, BOTTOM_DEFAULTS.waist_width) * 110;
      const hipW = clampScale(m.hip_width, BOTTOM_DEFAULTS.hip_width) * 130;
      const tw = clampScale(m.thigh_width, BOTTOM_DEFAULTS.thigh_width) * 60;
      const hmW = clampScale(m.hem_width, BOTTOM_DEFAULTS.hem_width) * 40;
      const tl = clampScale(m.total_length, BOTTOM_DEFAULTS.total_length) * 300;
      const riseH = clampScale(m.rise, BOTTOM_DEFAULTS.rise) * 80;

      const cx = 150;
      const topY = 40;
      const bottomY = topY + Math.min(tl, 350);
      const crotchY = topY + Math.min(riseH, (bottomY - topY) * 0.35);
      const hipY = topY + (crotchY - topY) * 0.5;
      const thighY = crotchY + (bottomY - crotchY) * 0.15;

      const waistL = cx - ww / 2;
      const waistR = cx + ww / 2;
      const hipL = cx - hipW / 2;
      const hipR = cx + hipW / 2;
      const gap = 6;
      const thighL = cx - gap - tw;
      const thighMid = cx - gap;
      const hemL = cx - gap - hmW;
      const hemMid = cx - gap;

      const leftEdge = Math.min(waistL, hipL, thighL, hemL);

      return { topY, bottomY, crotchY, hipY, thighY, waistL, waistR, hipL, hipR, thighL, thighMid, hemL, hemMid, leftEdge, cx, gap, ww, hipW, tw, hmW };
    });

    const bottomSvgPath = computed(() => {
      const c = btmCoords.value;
      const rHemOuter = c.cx + c.gap + c.hmW;

      return `M ${c.waistL},${c.topY}
        L ${c.waistR},${c.topY}
        Q ${c.hipR},${c.hipY} ${c.hipR},${c.hipY}
        L ${c.cx + c.gap + c.tw},${c.thighY}
        L ${rHemOuter},${c.bottomY}
        L ${c.cx + c.gap},${c.bottomY}
        L ${c.cx + c.gap},${c.crotchY + 10}
        Q ${c.cx},${c.crotchY} ${c.cx - c.gap},${c.crotchY + 10}
        L ${c.cx - c.gap},${c.bottomY}
        L ${c.hemL},${c.bottomY}
        L ${c.cx - c.gap - c.tw},${c.thighY}
        Q ${c.hipL},${c.hipY} ${c.hipL},${c.hipY}
        L ${c.waistL},${c.topY}
        Z`;
    });

    // ===== ONEPIECE coords =====
    const opCoords = computed(() => {
      const m = ms.value;
      const sw = clampScale(m.shoulder_width, ONEPIECE_DEFAULTS.shoulder_width) * 110;
      const cw = clampScale(m.chest_width, ONEPIECE_DEFAULTS.chest_width) * 120;
      const ww = clampScale(m.waist_width, ONEPIECE_DEFAULTS.waist_width) * 100;
      const hipW = clampScale(m.hip_width, ONEPIECE_DEFAULTS.hip_width) * 140;
      const tl = clampScale(m.total_length, ONEPIECE_DEFAULTS.total_length) * 300;
      const sl = clampScale(m.sleeve_length, ONEPIECE_DEFAULTS.sleeve_length) * 160;

      const cx = 150;
      const topY = 50;
      const bottomY = topY + Math.min(tl, 340);
      const chestY = topY + (bottomY - topY) * 0.18;
      const waistY = topY + (bottomY - topY) * 0.35;
      const hipY = topY + (bottomY - topY) * 0.5;

      const shoulderL = cx - sw / 2;
      const shoulderR = cx + sw / 2;
      const chestL = cx - cw / 2;
      const chestR = cx + cw / 2;
      const waistL = cx - ww / 2;
      const waistR = cx + ww / 2;
      const hipL = cx - hipW / 2;
      const hipR = cx + hipW / 2;
      const sleeveEndX = Math.min(shoulderR + sl * 0.3, 285);
      const sleeveEndY = Math.min(topY + sl * 0.88, waistY);
      const leftEdge = Math.min(shoulderL, chestL, waistL, hipL);

      return { topY, bottomY, chestY, waistY, hipY, shoulderL, shoulderR, chestL, chestR, waistL, waistR, hipL, hipR, sleeveEndX, sleeveEndY, leftEdge, sl };
    });

    const onepieceSvgPath = computed(() => {
      const c = opCoords.value;
      const cx = 150;
      const neckW = 12;
      const sleeveW = 16;
      const slR_x = c.sleeveEndX;
      const slR_y = c.sleeveEndY;
      const slL_x = Math.max(c.shoulderL - c.sl * 0.3, 15);
      const slL_y = slR_y;

      return `M ${cx - neckW},${c.topY}
        Q ${cx},${c.topY - 6} ${cx + neckW},${c.topY}
        L ${c.shoulderR},${c.topY}
        L ${slR_x},${slR_y}
        L ${slR_x - 4},${slR_y + sleeveW}
        L ${c.chestR},${c.chestY + 8}
        L ${c.waistR},${c.waistY}
        L ${c.hipR},${c.hipY}
        Q ${c.hipR + 10},${(c.hipY + c.bottomY) / 2} ${c.hipR + 4},${c.bottomY}
        L ${c.hipL - 4},${c.bottomY}
        Q ${c.hipL - 10},${(c.hipY + c.bottomY) / 2} ${c.hipL},${c.hipY}
        L ${c.waistL},${c.waistY}
        L ${c.chestL},${c.chestY + 8}
        L ${slL_x + 4},${slL_y + sleeveW}
        L ${slL_x},${slL_y}
        L ${c.shoulderL},${c.topY}
        Z`;
    });

    // ===== SHOES coords =====
    const shoeCoords = computed(() => {
      const m = ms.value;
      const fl = clampScale(m.foot_length, SHOES_DEFAULTS.foot_length) * 220;
      const bw = clampScale(m.ball_width, SHOES_DEFAULTS.ball_width) * 60;

      const heelX = 40;
      const toeX = heelX + Math.min(fl, 250);
      const bottomY = 140;
      const ballTopY = bottomY - Math.min(bw, 100);
      const ballX = heelX + fl * 0.55;

      return { heelX, toeX, bottomY, ballTopY, ballX };
    });

    const shoesSvgPath = computed(() => {
      const c = shoeCoords.value;
      const topY = c.ballTopY - 10;
      const heelTopY = topY + 10;

      return `M ${c.heelX},${c.bottomY}
        L ${c.heelX},${heelTopY}
        Q ${c.heelX},${topY} ${c.heelX + 20},${topY}
        L ${c.ballX - 20},${topY - 10}
        Q ${c.toeX - 10},${topY - 15} ${c.toeX},${c.bottomY - 20}
        Q ${c.toeX + 4},${c.bottomY - 5} ${c.toeX - 6},${c.bottomY}
        Z`;
    });

    const fetchClothing = async () => {
      const data = await closetApi.getClothing(Number(props.id));
      if (data) {
        clothing.value = data;
      }
    };

    const formatDate = (dateStr: any): string => {
      if (!dateStr) return "";
      if (Array.isArray(dateStr)) {
        const [y, mo, d] = dateStr;
        return `${y}.${String(mo).padStart(2, "0")}.${String(d).padStart(2, "0")}`;
      }
      const str = String(dateStr);
      const parts = str.split(/[-,./]/);
      if (parts.length < 3) return str;
      return `${parts[0]}.${String(parts[1]).padStart(2, "0")}.${String(parts[2]).padStart(2, "0")}`;
    };

    const fitClass = (v: string): string => {
      return (
        {
          VERY_TIGHT: "fit-t2",
          TIGHT: "fit-t1",
          PERFECT: "fit-ok",
          LOOSE: "fit-l1",
          VERY_LOOSE: "fit-l2",
        } as Record<string, string>
      )[v] || "";
    };

    const getMeasureUnit = (category: string, key: string): string => {
      const fields = MEASUREMENT_FIELDS[category];
      if (!fields) return "";
      const field = fields.find((f) => f.key === key);
      return field ? field.unit : "";
    };

    // 치수 목록 값/단위 (토글에 따라 인치 변환)
    const dispMeasureVal = (category: string, key: string, val: number): number =>
      convertMeasure(val, getMeasureUnit(category, key) || "cm", toInch.value);
    const dispMeasureUnit = (category: string, key: string): string =>
      displayUnit(getMeasureUnit(category, key) || "cm", toInch.value);

    const goToEdit = () => {
      router.push({
        name: "closet-clothing-edit",
        params: { id: props.id },
        query: wardrobeQuery(),
      });
    };

    const handleDelete = async () => {
      if (!confirm("삭제하시겠습니까?")) return;
      const data = await closetApi.deleteClothing(Number(props.id));
      if (data) {
        alert("삭제되었습니다.");
        router.push({ name: "closet-wardrobe", query: wardrobeQuery() });
      }
    };

    const goToList = () => {
      router.push({ name: "closet-wardrobe", query: wardrobeQuery() });
    };

    onMounted(() => {
      fetchClothing();
    });

    return {
      clothing,
      ms,
      svgCategory,
      hasMeasurements,
      formatDate,
      fitClass,
      getMeasureUnit,
      dispMeasureVal,
      dispMeasureUnit,
      toInch,
      toggleUnit,
      lenU,
      mmU,
      dcm,
      dmm,
      goToEdit,
      handleDelete,
      goToList,
      getCategoryLabel,
      getFitFeedbackLabel,
      getMeasureLabel,
      topCoords,
      topSvgPath,
      btmCoords,
      bottomSvgPath,
      opCoords,
      onepieceSvgPath,
      shoeCoords,
      shoesSvgPath,
    };
  },
});
</script>

<style lang="scss">
@import "../assets/css/closet-clothing.scss";
</style>
